/* eslint-disable max-lines -- Pre-existing violation */
/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {type ThunkDispatch, type UnknownAction} from '@reduxjs/toolkit';
import {
  Alert,
  Card,
  DataStatus,
  Dialog,
  DialogFooter,
  Flag,
  Form,
  FormButton,
  FormField,
  Search,
  Segment,
  Table,
  TableRow,
  openConfirmDialog,
  useFormContext,
  type FormControl,
  Separator,
} from 'platform/components';
import {Grid, HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {useState} from 'react';
import {Controller, useWatch, type Control} from 'react-hook-form';
import {useDispatch} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {
  newVehicleApi,
  useArchiveFeatureMutation,
  useArchivePackageMutation,
  useCreateFeatureRuleMutation,
  useDeleteFeatureRuleMutation,
  useGetAuthorConfiguratorBundleQuery,
  useGetVariantMatrixQuery,
  useListFeatureQuery,
  useListFeatureRuleQuery,
  useListPackageQuery,
  useListTrimQuery,
  useUpdateFeatureMutation,
  useUpdateFeatureRuleMutation,
  type FeatureResponseBody,
  type FeatureRuleResponseBody,
  type PackageResponseBody,
  type TrimResponseBody,
} from '@dms/api/new-vehicle';
import {
  FEATURE_CATEGORIES,
  NewVehicleStatusFlag,
  getFeatureCategoryOptions,
  getNewVehicleEnumLabel,
  refetchOnImageExpiry,
} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';

import type {ApiError} from 'shared/web';

import {useCatalogueVatRate} from '../hooks/useCatalogueVatRate';
import {useHeldVersion} from '../hooks/useHeldVersion';
import {type CatalogueState} from '../hooks/useMatrixSaveQueue';
import {usePublishedPriceSave} from '../hooks/usePublishedPriceSave';
import {useYearFeatureNames} from '../hooks/useYearFeatureNames';
import {ITEM_CHANGE_CANCELLED} from '../types/PricedItemChangeOutcome';
import {
  actionsColumn,
  archiveRowAction,
  Cell,
  column,
  editRowAction,
  handleCatalogueError,
  joinParts,
  optionalText,
  RowActions,
  TabIntro,
  t,
  FormSection,
  isStaleCatalogueVersion,
} from '../utils/catalogueUi';
import {hasAnyImage} from '../utils/hasAnyImage';
import {newerRecord} from '../utils/newerRecord';
import {packageArchiveChange} from '../utils/packageArchiveChange';
import {ruleSentence} from '../utils/ruleSentence';
import {variantChangeName} from '../utils/variantChangeName';
import {AddFromLibraryDialog} from './AddFromLibraryDialog';
import {CatalogueImageField} from './CatalogueImageField';
import {CatalogueThumbnail} from './CatalogueThumbnail';
import {NewCatalogueEquipment} from './NewCatalogueEquipment';
import {NewPricelistVersionDialog} from './NewPricelistVersionDialog';
import {PackageBuilder} from './PackageBuilder';
import {TrimEquipmentMatrix} from './TrimEquipmentMatrix';
import {TrimPrices} from './TrimPrices';

// Reads the stored item to build an archive entry. It keeps no subscription, so the refetch after
// the archive does not read the archived item again.
const READ_FROM_SERVER = {subscribe: false, forceRefetch: true};

const featureSchema = z.object({
  name: z.string().min(1),
  category: z.enum(FEATURE_CATEGORIES),
  kind: z.enum(['option', 'wheel', 'accessory', 'colour', 'upholstery']),
  fitStage: z.enum(['factory', 'dealer']),
  standaloneOrderable: z.boolean(),
  factoryCode: optionalText,
});
const ruleSchema = z.object({
  type: z.enum(['requires', 'excludes', 'autoAdd']),
  sourceFeatureId: z.string().min(1),
  targetFeatureIds: z.array(z.string()).min(1),
  ifSelectedFeatureIds: z.array(z.string()),
  trimIds: z.array(z.string()),
  note: optionalText,
});
type FeatureValues = z.infer<typeof featureSchema>;
type RuleValues = z.infer<typeof ruleSchema>;
const emptyCondition: FeatureRuleResponseBody['appliesIf'] = {
  anyOfVariantIds: [],
  allOfFeatureIds: [],
};
const ruleFeatureIds = (rule: FeatureRuleResponseBody) => [
  rule.sourceFeatureId,
  ...rule.targetFeatureIds,
  ...rule.ifSelectedFeatureIds,
  ...rule.appliesIf.allOfFeatureIds,
];
const conflictMessage = (error: unknown) => {
  if (typeof error !== 'object' || error === null) {
    return '';
  }
  const data = 'data' in error ? error.data : 'errors' in error ? error.errors : undefined; // eslint-disable-line no-nested-ternary -- Pre-existing violation
  if (typeof data !== 'object' || data === null) {
    return '';
  }
  if ('message' in data && typeof data.message === 'string') {
    return data.message;
  }
  if (!('errors' in data) || !Array.isArray(data.errors)) {
    return '';
  }
  const first = data.errors[0];
  return typeof first === 'object' &&
    first !== null &&
    'message' in first &&
    typeof first.message === 'string'
    ? first.message
    : '';
};

function FeatureKindAndCategoryFields({control}: {control: FormControl<FeatureValues>}) {
  const form = useFormContext<FeatureValues>();
  const kind = useWatch({control, name: 'kind'});
  return (
    <>
      <FormField
        control={control}
        name="category"
        type="choice"
        menuInPortal
        isNotClearable
        isDisabled={kind === 'wheel'}
        label={t('entity.newVehicle.catalogue.category', 'Category')}
        options={getFeatureCategoryOptions(t)}
      />
      <FormField
        control={control}
        name="kind"
        type="choice"
        menuInPortal
        label={t('entity.newVehicle.catalogue.kind', 'Kind')}
        options={['option', 'wheel', 'accessory', 'colour', 'upholstery'].map((value) => ({
          value,
          label: getNewVehicleEnumLabel('featureKind', value, t),
        }))}
        onChange={(value) => {
          // BE rejects a wheel set outside wheels_tyres (DMS-NV-CT-0038).
          if (value === 'wheel') {
            form.setValue('category', 'wheels_tyres', {shouldDirty: true});
          }
        }}
      />
    </>
  );
}

const toFeatureValues = (item: FeatureResponseBody): FeatureValues => ({
  name: item.name,
  category: item.category,
  kind: item.kind,
  fitStage: item.fitStage,
  standaloneOrderable: item.standaloneOrderable,
  factoryCode: item.factoryCode ?? '',
});

function FeatureDialog(props: {item: FeatureResponseBody; modelYearId?: string; done: () => void}) {
  const [item, setItem] = useState(props.item);
  const [update] = useUpdateFeatureMutation();
  const [getFeature] = newVehicleApi.useLazyGetFeatureQuery();
  const held = useHeldVersion({
    record: item,
    read: () => getFeature({featureId: item.id}, false).unwrap(),
    toValues: toFeatureValues,
    onRead: setItem,
  });
  const version = held.version ?? item.version;
  const featureList = useListFeatureQuery(
    props.modelYearId ? {modelYearId: props.modelYearId} : {}
  );
  const imageRecord = newerRecord(item, featureList.data?.items);
  const reloadStale = () => {
    void featureList.refetch();
    return held.reloadStale();
  };
  const submit = (values: FeatureValues) =>
    update({
      featureId: item.id,
      featureWriteRequestBody: {
        name: values.name,
        codes: item.codes,
        category: values.category,
        kind: values.kind,
        fitStage: values.fitStage,
        standaloneOrderable: values.standaloneOrderable,
        factoryCode: values.factoryCode || null,
        version,
      },
    })
      .unwrap()
      .then(props.done)
      .catch(async (error: ApiError) => {
        handleCatalogueError(error);
        if (isStaleCatalogueVersion(error)) {
          await reloadStale();
        }
      });
  return (
    <VStack spacing={4}>
      <FormSection title={t('entity.newVehicle.catalogue.image', 'Image')}>
        <CatalogueImageField
          owner="feature"
          ownerId={item.id}
          image={imageRecord.imageAssetRef}
          ownerVersion={imageRecord.version}
          version={version}
          onVersionChange={held.adoptVersion}
          onStale={() => void reloadStale()}
          onImageError={refetchOnImageExpiry(featureList)}
        />
      </FormSection>
      <Separator spacing={0} />
      <Form<FeatureValues>
        experimentalZodSchema={featureSchema}
        defaultValues={toFeatureValues(item)}
        onSubmit={submit}
      >
        {(control, form) => {
          held.formRef.current = form;
          return (
            <VStack spacing={4}>
              <Show when={isNotNil(props.modelYearId)}>
                <Alert
                  variant="info"
                  message={t(
                    'entity.newVehicle.catalogue.sharedLibraryItem',
                    'This item belongs to the equipment library. Changes apply to every model year that uses it.'
                  )}
                />
              </Show>
              <Grid columns={2} spacing={3}>
                <FormField
                  control={control}
                  name="name"
                  type="text"
                  label={t('general.labels.name', 'Name')}
                />
                <FeatureKindAndCategoryFields control={control} />
                <FormField
                  control={control}
                  name="fitStage"
                  type="choice"
                  menuInPortal
                  label={t('entity.newVehicle.catalogue.fitStage', 'Fit stage')}
                  helperText={t(
                    'entity.newVehicle.catalogue.dealerFitHelp',
                    'Dealer-fit equipment is never locked with the factory specification.'
                  )}
                  options={['factory', 'dealer'].map((value) => ({
                    value,
                    label: getNewVehicleEnumLabel('fitStage', value, t),
                  }))}
                />
                <FormField
                  control={control}
                  name="standaloneOrderable"
                  type="checkbox"
                  label={t(
                    'entity.newVehicle.catalogue.standaloneOrderable',
                    'Standalone orderable'
                  )}
                />
                <FormField
                  control={control}
                  name="factoryCode"
                  type="text"
                  label={t('entity.newVehicle.catalogue.factoryCode', 'Factory code')}
                />
              </Grid>
              <DialogFooter>
                <HStack justify="flex-end">
                  <FormButton
                    control={control}
                    type="button"
                    title={t('general.actions.save', 'Save')}
                    onClick={() => void control.submitForm()}
                  />
                </HStack>
              </DialogFooter>
            </VStack>
          );
        }}
      </Form>
    </VStack>
  );
}

const matchesEquipmentSearch = (row: FeatureResponseBody, term: string) =>
  [row.name, row.factoryCode, getNewVehicleEnumLabel('featureCategory', row.category, t)].some(
    (value) => value?.toLocaleLowerCase().includes(term)
  );

export function FeaturesTab({
  modelYearId,
  canWrite,
  canViewCost,
}: {
  modelYearId?: string;
  canWrite: boolean;
  canViewCost: boolean;
}) {
  const query = useListFeatureQuery(modelYearId ? {modelYearId} : {});
  const packages = useListPackageQuery({});
  const rules = useListFeatureRuleQuery({});
  const [archive] = useArchiveFeatureMutation();
  const [editing, setEditing] = useState<FeatureResponseBody | 'new' | 'library'>();
  const [references, setReferences] = useState('');
  const [search, setSearch] = useState<string | null>(null);
  const editedFeature = typeof editing === 'object' ? editing : undefined;
  const rows = query.data?.items.filter(({archived}) => !archived) ?? [];
  const term = search?.trim().toLocaleLowerCase() ?? '';
  const visibleRows = rows.filter((row) => matchesEquipmentSearch(row, term));
  const showImage = hasAnyImage(visibleRows);
  const archiveFeature = (row: FeatureResponseBody) =>
    openConfirmDialog({
      text: t('entity.newVehicle.confirmations.archiveFeature', 'Archive equipment item {name}?', {
        name: row.name,
      }),
      onConfirm: () =>
        void archive({featureId: row.id, versionedWriteRequestBody: {version: row.version}})
          .unwrap()
          .then(() => void query.refetch())
          .catch((error: ApiError) => {
            const message = conflictMessage(error);
            if (!message) {
              handleCatalogueError(error);
              return;
            }
            const packageReferences = (packages.data?.items ?? [])
              .filter((item) => !item.archived && item.memberFeatureIds.includes(row.id))
              .map(
                (item) =>
                  `${i18n.t('entity.newVehicle.catalogue.package', {defaultValue: 'Package'})}: ${item.name}`
              );
            const ruleReferences = (rules.data?.items ?? [])
              .filter(
                (item) =>
                  !item.archived &&
                  (item.sourceFeatureId === row.id ||
                    item.targetFeatureIds.includes(row.id) ||
                    item.ifSelectedFeatureIds.includes(row.id) ||
                    item.appliesIf.allOfFeatureIds.includes(row.id))
              )
              .map(
                (item) =>
                  `${i18n.t('entity.newVehicle.catalogue.rule', {defaultValue: 'Rule'})}: ${item.note || item.id}`
              );
            setReferences([message, ...packageReferences, ...ruleReferences].join('\n'));
          }),
    });
  return (
    <VStack spacing={4}>
      <TabIntro>
        {modelYearId
          ? t(
              'entity.newVehicle.catalogue.intro.yearEquipment',
              'The equipment of this model year: every item on one of its trims or in one of its packages. Add a new item or take one from the library, then set it on the Equipment by trim tab.'
            )
          : t(
              'entity.newVehicle.catalogue.intro.featureLibrary',
              "Everything that can be fitted to this car. Create items in the library, then decide on each model year's Equipment by trim tab which trims include them as standard or sell them as options."
            )}
      </TabIntro>
      <Card
        title={
          modelYearId
            ? t('entity.newVehicle.catalogue.equipment', 'Equipment')
            : t('entity.newVehicle.catalogue.featureLibrary', 'Equipment library')
        }
        actions={
          canWrite
            ? [
                ...(modelYearId
                  ? [
                      {
                        type: 'button' as const,
                        variant: 'secondary' as const,
                        title: t('entity.newVehicle.catalogue.addFromLibrary', 'Add from library'),
                        leftIcon: 'action/search' as const,
                        onClick: () => setEditing('library'),
                      },
                    ]
                  : []),
                {
                  type: 'button',
                  title: t('entity.newVehicle.catalogue.addFeature', 'Add equipment'),
                  leftIcon: 'content/add',
                  onClick: () => setEditing('new'),
                },
              ]
            : []
        }
      >
        <DataStatus
          isLoading={query.isLoading}
          isError={query.isError}
          isEmpty={!rows.length}
          emptyMessage={t('entity.newVehicle.catalogue.empty.features', 'No equipment yet')}
          emptySubheadline={
            modelYearId
              ? t(
                  'entity.newVehicle.catalogue.empty.yearFeaturesHint',
                  "Add a new item or take one from the library and place it on this model year's trims."
                )
              : t(
                  'entity.newVehicle.catalogue.empty.featuresHint',
                  'Add wheels, options and accessories, then place them on trims.'
                )
          }
          action={
            canWrite
              ? {
                  title: t('entity.newVehicle.catalogue.addFeature', 'Add equipment'),
                  leftIcon: 'content/add',
                  onClick: () => setEditing('new'),
                }
              : undefined
          }
        >
          <VStack spacing={4}>
            <Search
              value={search}
              onChange={setSearch}
              placeholder={t(
                'entity.newVehicle.authoring.searchEquipmentPlaceholder',
                'Search equipment…'
              )}
            />
            <DataStatus
              isEmpty={!visibleRows.length}
              emptyMessage={t('general.labels.noResultsFound', 'No results found')}
            >
              <Table
                columns={[
                  column(t('entity.newVehicle.catalogue.equipment', 'Equipment')),
                  column(t('entity.newVehicle.catalogue.howSold', 'How it is sold'), {
                    width: '30%',
                  }),
                  actionsColumn(),
                ]}
              >
                {visibleRows.map((row) => (
                  <TableRow key={row.id} onRowClick={canWrite ? () => setEditing(row) : undefined}>
                    <Cell>
                      <HStack spacing={3} align="center">
                        <CatalogueThumbnail
                          image={row.imageAssetRef}
                          reserveSlot={showImage}
                          onImageError={refetchOnImageExpiry(query)}
                        />
                        <VStack spacing={0}>
                          <Text size="small" alternative>
                            {row.name}
                          </Text>
                          <Text size="xSmall" color="secondary">
                            {joinParts([
                              getNewVehicleEnumLabel('featureCategory', row.category, t),
                              row.factoryCode ?? undefined,
                            ])}
                          </Text>
                        </VStack>
                      </HStack>
                    </Cell>
                    <Cell>
                      <HStack spacing={1} wrap>
                        <NewVehicleStatusFlag group="fitStage" value={row.fitStage} />
                        <Show when={!row.standaloneOrderable}>
                          <Flag
                            label={t('entity.newVehicle.rules.packageOnly', 'Package-only')}
                            colorScheme="neutral"
                            isSubtle
                          />
                        </Show>
                      </HStack>
                    </Cell>
                    <Cell>
                      <Show when={canWrite}>
                        <RowActions
                          actions={[
                            editRowAction(() => setEditing(row)),
                            ...(modelYearId ? [] : [archiveRowAction(() => archiveFeature(row))]),
                          ]}
                        />
                      </Show>
                    </Cell>
                  </TableRow>
                ))}
              </Table>
            </DataStatus>
          </VStack>
        </DataStatus>
      </Card>
      <Show when={editing === 'library' && modelYearId ? modelYearId : null}>
        {(yearId) => (
          <AddFromLibraryDialog
            modelYearId={yearId}
            yearFeatureIds={rows.map(({id}) => id)}
            onClose={() => setEditing(undefined)}
          />
        )}
      </Show>
      <Show when={editing === 'new'}>
        <NewCatalogueEquipment
          modelYearId={modelYearId}
          canViewCost={canViewCost}
          onClose={() => setEditing(undefined)}
          onSaved={() => setEditing(undefined)}
        />
      </Show>
      <Dialog
        isOpen={Boolean(editedFeature)}
        onClose={() => setEditing(undefined)}
        withAdditionalFooter
        title={
          editedFeature
            ? t('entity.newVehicle.catalogue.editEquipmentNamed', 'Edit equipment · {name}', {
                name: editedFeature.name,
              })
            : ''
        }
      >
        <Show when={editedFeature}>
          {(item) => (
            <FeatureDialog
              item={item}
              modelYearId={modelYearId}
              done={() => setEditing(undefined)}
            />
          )}
        </Show>
      </Dialog>
      <Dialog
        isOpen={Boolean(references)}
        onClose={() => setReferences('')}
        title={t('entity.newVehicle.catalogue.archiveBlocked', 'Archive blocked')}
      >
        <Text>{references}</Text>
      </Dialog>
    </VStack>
  );
}

export function EquipmentByTrimTab(props: {
  modelYearId: string;
  currency: string;
  canWrite: boolean;
  canViewCost: boolean;
}) {
  const query = useListFeatureQuery({modelYearId: props.modelYearId});
  const bundle = useGetAuthorConfiguratorBundleQuery({modelYearId: props.modelYearId});
  const trims = useListTrimQuery({});
  const vatRate = useCatalogueVatRate(props.modelYearId);
  const rows = query.data?.items.filter(({archived}) => !archived) ?? [];
  const yearTrims =
    trims.data?.items.filter((trim) => trim.modelYearId === props.modelYearId && !trim.archived) ??
    [];
  return (
    <DataStatus
      isLoading={query.isLoading || bundle.isLoading || trims.isLoading}
      // A failed refetch keeps the matrix and its unsaved drafts; only a failed first load errors.
      isError={
        (query.isError && isNil(query.data)) ||
        (bundle.isError && isNil(bundle.data)) ||
        (trims.isError && isNil(trims.data))
      }
    >
      <TrimEquipmentMatrix
        modelYearId={props.modelYearId}
        trims={yearTrims}
        features={rows}
        variants={(bundle.data?.variants ?? []).map(({id, orderCode, trimId, engineId}) => ({
          id,
          orderCode,
          trimId,
          name: variantChangeName(
            bundle.data?.trims.find((trim) => trim.id === trimId)?.name ?? trimId,
            bundle.data?.engines.find((engine) => engine.id === engineId)?.name ?? engineId,
            orderCode
          ),
        }))}
        currency={props.currency}
        vatRate={vatRate}
        canWrite={props.canWrite}
        canViewCost={props.canViewCost}
      />
    </DataStatus>
  );
}

export function PackagesTab({
  modelYearId,
  currency,
  canWrite,
  canViewCost,
}: {
  modelYearId: string;
  currency: string;
  canWrite: boolean;
  canViewCost: boolean;
}) {
  const query = useListPackageQuery({});
  const features = useListFeatureQuery({modelYearId});
  const trims = useListTrimQuery({});
  const [archive] = useArchivePackageMutation();
  const dispatch = useDispatch<ThunkDispatch<CatalogueState, unknown, UnknownAction>>();
  const publishedPrice = usePublishedPriceSave(modelYearId);
  const vatRate = useCatalogueVatRate(modelYearId);
  const [editing, setEditing] = useState<PackageResponseBody | 'new'>();
  const rows =
    query.data?.items.filter(
      (item) => item.modelYearId === modelYearId && !item.archived && item.status !== 'archived'
    ) ?? [];
  const featureRows = features.data?.items.filter((item) => !item.archived) ?? [];
  const trimRows =
    trims.data?.items.filter((item) => item.modelYearId === modelYearId && !item.archived) ?? [];
  const archivePackage = (row: PackageResponseBody) =>
    openConfirmDialog({
      text: t('entity.newVehicle.confirmations.archivePackage', 'Archive package {name}?', {
        name: row.name,
      }),
      onConfirm: () =>
        void publishedPrice
          .writePricedItemChange({
            // A draft package is outside the price list and needs no pricelist version.
            ...packageArchiveChange(row, trimRows, (packageId) =>
              dispatch(
                newVehicleApi.endpoints.getPackage.initiate({packageId}, READ_FROM_SERVER)
              ).unwrap()
            ),
            write: (pricelistVersion) =>
              archive({
                packageId: row.id,
                pricedItemArchiveRequestBody: {
                  version: row.version,
                  ...(pricelistVersion && {pricelistVersion}),
                },
              }).unwrap(),
          })
          .then((outcome) => {
            if (outcome !== ITEM_CHANGE_CANCELLED) {
              void query.refetch();
            }
          })
          .catch(handleCatalogueError),
    });
  if (editing) {
    return (
      // The builder reads library taxonomy once on mount; an unloaded list would read as "orderable alone".
      <DataStatus isLoading={features.isLoading} isError={features.isError}>
        <PackageBuilder
          modelYearId={modelYearId}
          item={editing === 'new' ? undefined : editing}
          features={featureRows}
          trims={trimRows}
          currency={currency}
          canViewCost={canViewCost}
          onClose={() => setEditing(undefined)}
        />
      </DataStatus>
    );
  }
  return (
    <VStack spacing={3}>
      <TabIntro>
        {t(
          'entity.newVehicle.catalogue.intro.packagesPerTrim',
          'Bundles of equipment sold together, at their own price on each trim. Only active packages appear in the configurator.'
        )}
      </TabIntro>
      <Card
        title={t('entity.newVehicle.catalogue.packages', 'Packages')}
        actions={
          canWrite
            ? [
                {
                  type: 'button',
                  title: t('entity.newVehicle.catalogue.addPackage', 'Add package'),
                  leftIcon: 'content/add',
                  onClick: () => setEditing('new'),
                },
              ]
            : []
        }
      >
        <DataStatus
          isLoading={query.isLoading}
          isError={query.isError}
          isEmpty={!rows.length}
          emptyMessage={t('entity.newVehicle.authoring.noPackages', 'No packages yet')}
          emptySubheadline={t(
            'entity.newVehicle.catalogue.empty.packagesHint',
            'Group equipment that is usually bought together, like a winter or tech pack.'
          )}
          action={
            canWrite
              ? {
                  title: t('entity.newVehicle.catalogue.addPackage', 'Add package'),
                  leftIcon: 'content/add',
                  onClick: () => setEditing('new'),
                }
              : undefined
          }
        >
          <Table
            columns={[
              column(t('entity.newVehicle.catalogue.package', 'Package'), {width: '22%'}),
              column(t('entity.newVehicle.catalogue.includes', 'Includes')),
              column(t('entity.newVehicle.catalogue.priceByTrim', 'Price by trim'), {
                width: '30%',
              }),
              column(t('entity.newVehicle.catalogue.status', 'Status'), {width: '10%'}),
              actionsColumn(),
            ]}
          >
            {rows.map((row) => (
              <TableRow key={row.id} onRowClick={canWrite ? () => setEditing(row) : undefined}>
                <Cell>
                  <VStack spacing={0}>
                    <Text size="small" alternative>
                      {row.name}
                    </Text>
                    <Text size="xSmall" color="secondary">
                      {row.codes[0]}
                    </Text>
                  </VStack>
                </Cell>
                <Cell>
                  <HStack spacing={1} wrap>
                    {row.memberFeatureIds.map((id) => (
                      <Flag
                        key={id}
                        label={featureRows.find((item) => item.id === id)?.name ?? id}
                        colorScheme="neutral"
                        isSubtle
                      />
                    ))}
                  </HStack>
                </Cell>
                <Cell>
                  <TrimPrices
                    rows={row.trims}
                    trims={trimRows}
                    currency={currency}
                    vatRate={vatRate}
                    canViewCost={canViewCost}
                  />
                </Cell>
                <Cell>
                  <NewVehicleStatusFlag group="packageStatus" value={row.status ?? 'draft'} />
                </Cell>
                <Cell>
                  <Show when={canWrite}>
                    <RowActions
                      actions={[
                        editRowAction(() => setEditing(row)),
                        archiveRowAction(() => archivePackage(row)),
                      ]}
                    />
                  </Show>
                </Cell>
              </TableRow>
            ))}
          </Table>
        </DataStatus>
        <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
      </Card>
    </VStack>
  );
}

function RuleTypeSegment(props: {control: Control<RuleValues>}) {
  return (
    <Controller
      control={props.control}
      name="type"
      render={({field}) => (
        <Segment
          label={t('entity.newVehicle.catalogue.ruleType', 'Rule type')}
          value={field.value}
          options={['requires', 'excludes', 'autoAdd'].map((value) => ({
            value,
            label: getNewVehicleEnumLabel('ruleType', value, t),
          }))}
          onChange={field.onChange}
        />
      )}
    />
  );
}
function RulePreview(props: {
  control: Control<RuleValues>;
  features: FeatureResponseBody[];
  trims: TrimResponseBody[];
}) {
  const values = useWatch({control: props.control});
  const parsed = ruleSchema.safeParse(values);
  if (!parsed.success) {
    return null;
  }
  return (
    <>
      <Alert variant="info" message={ruleSentence(parsed.data, props.features, props.trims)} />
      <Show when={parsed.data.type === 'excludes'}>
        <Text size="small" color="secondary">
          {t(
            'entity.newVehicle.rules.excludesSymmetric',
            'Excludes rules apply in both directions.'
          )}
        </Text>
      </Show>
    </>
  );
}

function RuleVariantCondition(props: {modelYearId: string; variantIds: string[]}) {
  const matrix = useGetVariantMatrixQuery({modelYearId: props.modelYearId});
  const orderCodes =
    matrix.data?.cells
      .filter(({id, orderCode}) => props.variantIds.includes(id) && orderCode)
      .map(({orderCode}) => orderCode) ?? [];
  return (
    <Text size="small" color="secondary">
      {orderCodes.length === props.variantIds.length
        ? t('entity.newVehicle.rules.onlyForVariants', 'Only for variants: {variants}', {
            variants: orderCodes.join(', '),
          })
        : t('entity.newVehicle.rules.onlyForVariantCount', 'Only for selected variants ({count})', {
            count: props.variantIds.length,
          })}
    </Text>
  );
}

function RuleOutsideYearWarning(props: {
  control: Control<RuleValues>;
  conditionFeatureIds: string[];
  outsideIds: string[];
}) {
  const values = useWatch({control: props.control});
  const fieldFeatureIds = [
    values.sourceFeatureId,
    ...(values.targetFeatureIds ?? []),
    ...(values.ifSelectedFeatureIds ?? []),
  ];
  const message = match({
    isConditionOutside: props.conditionFeatureIds.some((id) => props.outsideIds.includes(id)),
    isFieldOutside: fieldFeatureIds.filter(isNotNil).some((id) => props.outsideIds.includes(id)),
  })
    .with({isConditionOutside: true}, () =>
      t(
        'entity.newVehicle.rules.outsideYearConditionWarning',
        'Its condition uses equipment this model year does not use. Delete the rule and create it again.'
      )
    )
    .with({isFieldOutside: true}, () =>
      t(
        'entity.newVehicle.rules.outsideYearWarning',
        'Uses equipment this model year does not use. Saving is refused until it is replaced.'
      )
    )
    .otherwise(() => null);
  if (isNil(message)) {
    return null;
  }
  return <Alert variant="warning" message={message} />;
}

function RuleDialog(props: {
  modelYearId: string;
  item?: FeatureRuleResponseBody;
  features: FeatureResponseBody[];
  namedFeatures: FeatureResponseBody[];
  trims: TrimResponseBody[];
  outsideIds: string[];
  done: () => void;
}) {
  const [create] = useCreateFeatureRuleMutation();
  const [update] = useUpdateFeatureRuleMutation();
  const initialValues: RuleValues = {
    type: props.item?.type ?? 'requires',
    sourceFeatureId: props.item?.sourceFeatureId ?? '',
    targetFeatureIds: props.item?.targetFeatureIds ?? [],
    ifSelectedFeatureIds: props.item?.ifSelectedFeatureIds ?? [],
    trimIds: props.item?.trimIds ?? [],
    note: props.item?.note ?? '',
  };
  const condition = props.item?.appliesIf ?? emptyCondition;
  const fieldOptions = (savedIds: string[]) =>
    props.namedFeatures
      .filter(
        ({id}) => props.features.some((feature) => feature.id === id) || savedIds.includes(id)
      )
      .map(({id, name}) => ({value: id, label: name}));
  const yearTrims = props.trims.filter((trim) => trim.modelYearId === props.modelYearId);
  const featureName = (id: string) =>
    props.namedFeatures.find((feature) => feature.id === id)?.name ?? id;
  const trimOptions = props.trims
    .filter(
      ({id, archived, modelYearId: trimModelYearId}) =>
        (trimModelYearId === props.modelYearId && !archived) || initialValues.trimIds.includes(id)
    )
    .map((trim) => ({
      value: trim.id,
      label: match(trim)
        .when(
          ({modelYearId: trimModelYearId}) => trimModelYearId !== props.modelYearId,
          ({name}) =>
            t('entity.newVehicle.rules.otherYearTrim', '{name} (not in this model year)', {
              name,
            })
        )
        .with({archived: true}, ({name}) =>
          t('entity.newVehicle.rules.archivedTrim', '{name} (archived)', {name})
        )
        .otherwise(({name}) => name),
    }));
  const submit = async (values: RuleValues) => {
    const body = {
      modelYearId: props.modelYearId,
      ...values,
      note: values.note || undefined,
      appliesIf: condition,
      version: props.item?.version ?? 1,
    };
    await (
      props.item
        ? update({featureRuleId: props.item.id, featureRuleWriteRequestBody: body})
        : create({featureRuleWriteRequestBody: body})
    )
      .unwrap()
      .then(props.done)
      .catch(handleCatalogueError);
  };
  return (
    <Form<RuleValues>
      experimentalZodSchema={ruleSchema}
      defaultValues={initialValues}
      onSubmit={submit}
    >
      {(control) => (
        <VStack spacing={3}>
          <RuleOutsideYearWarning
            control={control}
            conditionFeatureIds={condition.allOfFeatureIds}
            outsideIds={props.outsideIds}
          />
          <RuleTypeSegment control={control} />
          <FormField
            control={control}
            name="sourceFeatureId"
            type="choice"
            menuInPortal
            label={t('entity.newVehicle.catalogue.source', 'Source')}
            options={fieldOptions([initialValues.sourceFeatureId])}
          />
          <FormField
            control={control}
            name="targetFeatureIds"
            type="multiChoice"
            menuInPortal
            label={t('entity.newVehicle.catalogue.targets', 'Targets')}
            options={fieldOptions(initialValues.targetFeatureIds)}
          />
          <FormField
            control={control}
            name="ifSelectedFeatureIds"
            type="multiChoice"
            menuInPortal
            label={t('entity.newVehicle.catalogue.ifSelected', 'If selected')}
            options={fieldOptions(initialValues.ifSelectedFeatureIds)}
          />
          <FormField
            control={control}
            name="trimIds"
            type="multiChoice"
            menuInPortal
            label={t('entity.newVehicle.rules.trims', 'Trims')}
            placeholder={t('entity.newVehicle.catalogue.allTrims', 'All trims')}
            options={trimOptions}
          />
          <Show when={condition.anyOfVariantIds.length > 0 ? condition.anyOfVariantIds : null}>
            {(variantIds) => (
              <RuleVariantCondition modelYearId={props.modelYearId} variantIds={variantIds} />
            )}
          </Show>
          <Show when={condition.allOfFeatureIds.length > 0 ? condition.allOfFeatureIds : null}>
            {(featureIds) => (
              <Text size="small" color="secondary">
                {t('entity.newVehicle.rules.onlyWhenSelected', 'Only when selected: {features}', {
                  features: featureIds.map(featureName).join(', '),
                })}
              </Text>
            )}
          </Show>
          <RulePreview control={control} features={props.namedFeatures} trims={yearTrims} />
          <FormField
            control={control}
            name="note"
            type="textarea"
            label={t('general.labels.note', 'Note')}
          />
          <DialogFooter>
            <HStack justify="flex-end">
              <FormButton
                control={control}
                type="button"
                title={t('general.actions.save', 'Save')}
                onClick={() => void control.submitForm()}
              />
            </HStack>
          </DialogFooter>
        </VStack>
      )}
    </Form>
  );
}
export function RulesTab({modelYearId, canWrite}: {modelYearId: string; canWrite: boolean}) {
  const query = useListFeatureRuleQuery({});
  const features = useListFeatureQuery({modelYearId});
  const trims = useListTrimQuery({});
  const [remove] = useDeleteFeatureRuleMutation();
  const [editing, setEditing] = useState<FeatureRuleResponseBody | 'new'>();
  const [deleting, setDeleting] = useState<FeatureRuleResponseBody>();
  // currentData: after a switch to another model year, `data` still holds the previous year's list.
  const featureRows = features.currentData?.items.filter((item) => !item.archived) ?? [];
  const rows =
    query.data?.items.filter((item) => item.modelYearId === modelYearId && !item.archived) ?? [];
  const namedFeatures = useYearFeatureNames(
    featureRows,
    features.currentData ? rows.flatMap(ruleFeatureIds) : []
  );
  const canEdit = canWrite && !namedFeatures.isError;
  const yearTrims = trims.data?.items.filter((trim) => trim.modelYearId === modelYearId) ?? [];
  const usesOutsideEquipment = (rule: FeatureRuleResponseBody) =>
    ruleFeatureIds(rule).some((id) => namedFeatures.outsideIds.includes(id));
  return (
    <VStack spacing={3}>
      <TabIntro>
        {t(
          'entity.newVehicle.catalogue.intro.rules',
          'Rules stop impossible orders: equipment that needs other equipment, items that cannot be combined, and items that are added automatically.'
        )}
      </TabIntro>
      <Card
        title={t('entity.newVehicle.catalogue.rules', 'Rules')}
        actions={
          canWrite
            ? [
                {
                  type: 'button',
                  title: t('entity.newVehicle.catalogue.addRule', 'Add rule'),
                  leftIcon: 'content/add',
                  onClick: () => setEditing('new'),
                },
              ]
            : []
        }
      >
        <Show when={namedFeatures.isError}>
          <Alert
            variant="warning"
            message={t(
              'entity.newVehicle.catalogue.outsideEquipmentNamesFailed',
              'Equipment this model year no longer uses could not be loaded, so this field cannot be changed. Reload the page to try again.'
            )}
          />
        </Show>
        <DataStatus
          isLoading={
            query.isLoading ||
            (features.isFetching && isNil(features.currentData)) ||
            namedFeatures.isLoading ||
            trims.isLoading
          }
          isError={query.isError || (trims.isError && isNil(trims.data))}
          isEmpty={!rows.length}
          emptyMessage={t('entity.newVehicle.catalogue.empty.rules', 'No rules yet')}
          emptySubheadline={t(
            'entity.newVehicle.catalogue.empty.rulesHint',
            'Without rules, customers can combine any equipment freely.'
          )}
          action={
            canWrite
              ? {
                  title: t('entity.newVehicle.catalogue.addRule', 'Add rule'),
                  leftIcon: 'content/add',
                  onClick: () => setEditing('new'),
                }
              : undefined
          }
        >
          <Table
            columns={[
              column(t('entity.newVehicle.catalogue.rule', 'Rule')),
              column(t('entity.newVehicle.catalogue.type', 'Type'), {width: '16%'}),
              actionsColumn(),
            ]}
          >
            {rows.map((row) => (
              <TableRow key={row.id} onRowClick={canEdit ? () => setEditing(row) : undefined}>
                <Cell>
                  <VStack spacing={0}>
                    <HStack spacing={2} align="center" wrap>
                      <Text size="small">
                        {ruleSentence(row, namedFeatures.features, yearTrims)}
                      </Text>
                      <Show when={usesOutsideEquipment(row)}>
                        <Flag
                          label={t(
                            'entity.newVehicle.rules.outsideYearFlag',
                            'Equipment not in this model year'
                          )}
                          colorScheme="orange"
                          isSubtle
                        />
                      </Show>
                    </HStack>
                    <Show when={Boolean(row.note)}>
                      <Text size="xSmall" color="secondary">
                        {row.note}
                      </Text>
                    </Show>
                  </VStack>
                </Cell>
                <Cell>
                  <NewVehicleStatusFlag group="ruleType" value={row.type} />
                </Cell>
                <Cell>
                  <Show when={canWrite}>
                    <RowActions
                      actions={[
                        ...(canEdit ? [editRowAction(() => setEditing(row))] : []),
                        {
                          icon: 'action/delete',
                          label: t('general.actions.delete', 'Delete'),
                          onClick: () => setDeleting(row),
                        },
                      ]}
                    />
                  </Show>
                </Cell>
              </TableRow>
            ))}
          </Table>
        </DataStatus>
        <Dialog
          isOpen={Boolean(deleting)}
          onClose={() => setDeleting(undefined)}
          title={t('entity.newVehicle.catalogue.deleteRuleConfirm', 'Delete this rule?')}
          buttons={[
            {
              title: t('general.actions.cancel', 'Cancel'),
              variant: 'secondary',
              onClick: () => setDeleting(undefined),
            },
            {
              title: t('general.actions.delete', 'Delete'),
              onClick: () => {
                if (!deleting) {
                  return;
                }
                void remove({
                  featureRuleId: deleting.id,
                  versionedWriteRequestBody: {version: deleting.version},
                })
                  .unwrap()
                  .then(() => {
                    setDeleting(undefined);
                    void query.refetch();
                  })
                  .catch(handleCatalogueError);
              },
            },
          ]}
        >
          <Text>
            {t('entity.newVehicle.catalogue.deleteRuleWarning', 'This cannot be undone.')}
          </Text>
        </Dialog>
        <Dialog
          isOpen={Boolean(editing)}
          onClose={() => setEditing(undefined)}
          withAdditionalFooter
          title={
            editing === 'new'
              ? t('entity.newVehicle.catalogue.addRule', 'Add rule')
              : t('entity.newVehicle.catalogue.editRule', 'Edit rule')
          }
        >
          {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
          {editing ? (
            <RuleDialog
              modelYearId={modelYearId}
              item={editing === 'new' ? undefined : editing}
              features={featureRows}
              namedFeatures={namedFeatures.features}
              trims={trims.data?.items ?? []}
              outsideIds={namedFeatures.outsideIds}
              done={() => {
                setEditing(undefined);
                void query.refetch();
              }}
            />
          ) : null}
        </Dialog>
      </Card>
    </VStack>
  );
}
